import { ReactComponent as IconArrowDown } from "@/assets/svgs/chevron-down-small.svg";
import { ReactComponent as IconActivity } from '@/assets/svgs/veridapt-activity-icon.svg';
import { ReactComponent as IconDataElement } from '@/assets/svgs/veridapt-data-element-small.svg';
import { ReactComponent as IconMilestonesHeader } from '@/assets/svgs/veridapt-milestone-flag-big.svg';
import { ReactComponent as IconMilestonesFlag } from '@/assets/svgs/veridapt-milestone-flag.svg';
import QuickLinkStepper from "@/components/QuickLinkStepper/index";
import useConfig from '@/hooks/use-config';
import usePackages from '@/hooks/use-packages/index.js';
import useStaticData from "@/hooks/use-static-data/index";
import apiFiles from "@/services/api/files.js";
import { createRef, useEffect, useState } from "react";
import MultiClamp from "react-multi-clamp";
import { useSelector } from "react-redux";
import { useNavigate } from '@/lib/router-dom';
import { checkStatus, checkUserType } from "../../utils/check-status.js";
import PackageReceivingDataElementCard from "../package-receiving-data-element-card.jsx";
import VeridaptDataElementCard from "./component/veridapt-data-element-card.jsx";
import { DATA_ElEMENT_TYPE } from '@/constants/package-sharing/index.js';
import Ajv from "ajv";
import addFormats from "@/utils/custom-ajv-formats";
import PropTypes from 'prop-types';

const ajv = new Ajv({ allErrors: true });
addFormats(ajv);

const VeridaptDataView = ({ currentPackage, reload, currentFolder }) => {

	/*=================================================
		General
	/================================================*/

	const { goTo } = useNavigate();
	const { provideItems } = useConfig();
	const staticData = useStaticData();
	const orgDetail = staticData.orgs;
	const user = useSelector(state => state.auth.user)

	const [activeStep, setActiveStep] = useState("")
	const [activeScroll, setActiveScroll] = useState("")
	const [selectedMilestone, setSelectedMilestone] = useState("")
	const [packageDeIsOpen, setPackageDeIsOpen] = useState(false)
	const [dataElementList, setDataElementList] = useState([])

	const { updatePackage } = usePackages()

	const FLOW_TYPE = {
		produces: "to",
		consumes: "from"
	}

	const milestonesList = currentPackage.milestones ?? []
	const isPackageCopy = currentPackage.receivingType === 'COPY'
	const packageDataElement = dataElementList?.filter(dataElement => dataElement.parent_node_id === currentPackage.id)

	function getOrgDetails(orgId) {
		const participant = orgDetail.find(
			(org) => org.orgId === orgId
		);

		return [{...participant, id: participant.orgId}] || [];
	}

	/*=================================================
		QuickLink stepper and scroll to view function
	/================================================*/

	// first milestone is in active state by default
	useEffect(() => {
		setSelectedMilestone(milestonesList[0]?.id)
		if(milestonesList[0]?.participantOrgIds?.some(participant => participant.id === user.organizationId)){
			setActiveStep(milestonesList[0]?.id)
		}
	}, [])

	function scrollToSelectedView(itemId){
		const barHeight = "90px"
		refs[itemId].current.style.scrollMarginTop = barHeight
		refs[itemId].current.scrollIntoView({
			behavior: "smooth"
		})
	}

	//scroll to and open the selected milestone when click from quicklink stepper
	function quickLinkSetActiveStep(itemId){
		const activeMilestone = milestonesList?.find(milestone => milestone.id === itemId)

		if(activeMilestone?.participantOrgIds?.some(participant => participant.id === user.organizationId)){
			 setActiveStep(itemId)
		} else {
			setActiveStep("")
		}

		if(activeScroll === itemId){
			return scrollToSelectedView(itemId)
		}

		setSelectedMilestone(itemId)
		return setActiveScroll(itemId)
	}

	function toggleExpand(selectedItemId, participantOrgIds){
		if(milestonesList.length <= 1){
			return
		}
		if(!participantOrgIds || participantOrgIds?.every(participant => participant.id !== user.organizationId)){
			return
		}
		if (selectedItemId === activeStep) {
			setActiveScroll("")
			setSelectedMilestone("")
			return setActiveStep("")
		}
		setSelectedMilestone(selectedItemId)
		setActiveScroll(selectedItemId)
		setActiveStep(selectedItemId)
	}

	const refs = milestonesList.reduce((acc, value)=>{
		acc[value.id] = createRef()
		return acc
	},{})

	useEffect(()=>{
		if(activeScroll){
			scrollToSelectedView(activeScroll)
		}
	},[activeScroll])

	/*=================================================
		API function
	/================================================*/

	async function packageUpdate(updatedDataElementList, dataElmentId, nodeId, dataElementType) {
		await updatePackage(currentPackage.id, {
			dataElements: updatedDataElementList.filter(
				(dataElement) => dataElement.dataElementId === dataElmentId && dataElement.parent_node_id === nodeId && dataElement.type === dataElementType
			)?.map(
				(dataElement) => {
					let _dataElement = {
						dataElementId: dataElement.dataElementId,
						parent_node_id: dataElement.parent_node_id,
						parent_node_type: dataElement.parent_node_type,
						recipientOrgId: dataElement?.selectedRecipient[0]?.id,
						onBehalfOrgId: dataElement?.selectedOnBehalfOf[0]?.id,
					}
					if (dataElement.savedPayloadFilename)
						_dataElement['sourceFilename'] = dataElement.savedPayloadFilename;
					if (dataElement.savedPayload)
						_dataElement['payload'] = dataElement.savedPayload;
					if (dataElement.savedPayloadSource)
						_dataElement['source'] = dataElement.savedPayloadSource;

					return _dataElement
				}
			)
		})

		reload()
	}


	/*=================================================
		Check function
	/================================================*/

	function validateData(json, schema) {
		const validate = ajv.compile(schema);
		if(!Array.isArray(json)){
			return validate(json)
		}
		return json.every(item => validate(item));
	}

	/*=================================================
		Get Participant and OnbehalfOf list
	/================================================*/

	function getAllowedParticipantList(config, flowType, dataElmentId, allowedParticipant, packageDataElementType, recipientOrgId) {
		const dataElement = config?.find(dataElement => dataElement.id === dataElmentId)

		if (!dataElement) {
			return []
		}

		if(packageDataElementType === DATA_ElEMENT_TYPE.receiver_send){
			return dataElement[flowType]?.filter(org => org.id === recipientOrgId)
		}

		return dataElement[flowType]?.filter(org => allowedParticipant?.includes(org.id) && org.id !== user?.organizationId)
	}

	function getOnBehalfList(allowedParticipants, selectedParticipant) {
		const participant = allowedParticipants?.find(item => item.id === selectedParticipant[0]?.id)

		if (!participant) {
			return []
		}
		return participant.on_behalf_of?.filter(org => user?.organizationId !== org.id ) || []
	}

	/*=================================================
		Compose Data Element objects
	/================================================*/

	useEffect(() => {
			const updatedDataElementList = currentPackage.dataElements?.map((dataElement) => {
				//const isPayloadAdded = (dataElement.savedPayload?.length > 0 && Object.keys(dataElement.savedPayload[0]).length > 0) || (dataElement.savedPayload && Object.keys(dataElement.savedPayload).length > 0)

				let isDownloadable = false
				if((dataElement?.messageStoreId || (dataElement?.messageStoreIds && dataElement?.messageStoreIds?.length > 0))){
					isDownloadable = true
				}

				const participantList = getAllowedParticipantList(provideItems, FLOW_TYPE.produces, dataElement.dataElementId, dataElement.allowedParticipantOrgIds, dataElement.packageDataElementType, dataElement.recipientOrgId)
				const selectedRecipient = dataElement.recipientOrgId ? getOrgDetails(dataElement.recipientOrgId) : []
				const onBehalfList = selectedRecipient.length === 1 ? getOnBehalfList(participantList, selectedRecipient) : [];

				let selectedOnBehalfOf = []
				if(dataElement.onBehalfOrgId){
					const selectedOnbehalfList = onBehalfList?.find(org => org.id === dataElement.onBehalfOrgId)
					selectedOnBehalfOf = selectedOnbehalfList ? [selectedOnbehalfList] : []
				} else {
					if(onBehalfList.length === 1){
						selectedOnBehalfOf = onBehalfList
						dataElement.onBehalfOrgId= onBehalfList[0].id
					}
				}

				return {
					...dataElement,
					name: dataElement.elementName,
					action: {
						type: dataElement.savedPayloadSource === "CSV" ? "UPLOAD" : dataElement.savedPayloadSource,
						value: dataElement.savedPayloadFilename || ""
					},
					participantList,
					onBehalfList,
					selectedRecipient,
					selectedOnBehalfOf,
					status: checkStatus(dataElement),
					isDownloadable: isDownloadable,
					isPackageSent: dataElement.messageStoreId ? true : false,
					userType: checkUserType(user?.organizationId,dataElement.contributorOrg, dataElement.consumerOrg, dataElement.providerOrg),
					consumerDetail: dataElement.consumerOrg ? getOrgDetails(dataElement.consumerOrg.id) : [],
					providerDetail:dataElement.providerOrg ? getOrgDetails(dataElement.providerOrg.id) : [],
					contributorDetail:dataElement.contributorOrg ? getOrgDetails(dataElement.contributorOrg.id) : [],
					initalRecipient:selectedRecipient
				}
			})
			setDataElementList(updatedDataElementList)
	}, [currentPackage, provideItems])

	/*=================================================
		Handle selection change function
	/================================================*/

	function packageRecipientChange(option, dataElementId, nodeId, dataElementType) {

		// get onBehalf list for respective data element base on selected participant
		if (option?.length > 0) {
			let onBehalfList = []
			let isOnBehalfOf = false;

			const updatedDataElementList = dataElementList.map(dataElement => {

				if (dataElement.dataElementId === dataElementId && dataElement.parent_node_id === nodeId && dataElement.type === dataElementType) {

					onBehalfList = getOnBehalfList(dataElement.participantList, option)
					if((dataElement.isOnBehalfOf && onBehalfList.length > 1)|| (!dataElement.isOnBehalfOf && onBehalfList.length > 1)  ){
						isOnBehalfOf = true
					}
					return {
						...dataElement,
						selectedRecipient: option,
						onBehalfList: onBehalfList,
						selectedOnBehalfOf: onBehalfList.length === 1 ? onBehalfList : [],
						isOnBehalfOf: onBehalfList.length > 0 ? true : false
					}
				}
				return dataElement
			})
			setDataElementList(updatedDataElementList)
			if(!isOnBehalfOf ){
				packageUpdate(updatedDataElementList, dataElementId, nodeId, dataElementType)
			}
		}
	}

	function packageOnBehalfOfChange(option, dataElementId, nodeId, dataElementType) {
		if(option?.length <= 0 ){
			return
		}
		const updatedDataElementList = dataElementList.map(dataElement => {
			if (dataElement.dataElementId === dataElementId && dataElement.parent_node_id === nodeId && dataElement.type === dataElementType) {
				return {
					...dataElement,
					selectedOnBehalfOf: option,
				}
			}
			return dataElement
		})
		setDataElementList(updatedDataElementList)
		packageUpdate(updatedDataElementList, dataElementId, nodeId, dataElementType)
	}

	function handleDataStructureUpload(payload, file, dataElementId, nodeId, dataElementType) {

		const updatedDataElementList = dataElementList.map(dataElement => {
			if (dataElement.dataElementId === dataElementId && dataElement.parent_node_id === nodeId && dataElement.type === dataElementType) {
				return {
					...dataElement,
					savedPayload: payload,
					savedPayloadSource: "CSV",
					savedPayloadFilename: file.path,
					action: {
						type: "UPLOAD",
						value: file.path
					},
				}
			}
			return dataElement
		})
		setDataElementList(updatedDataElementList)
		packageUpdate(updatedDataElementList, dataElementId, nodeId, dataElementType)
	}

	async function handleAttachmentsUpload(file, dataElementId, nodeId, dataElementType) {

		if (!file) {
			return
		}

		const [error, uploadFiles] = await apiFiles.upload(file)

		if (error) {
			// set error
			return
		}

		if (uploadFiles[0].data.data?.length !== 1) {
			return
		}

		const { filename, file_content } = uploadFiles[0].data.data[0]
		if (!filename || !file_content) {
			return
		}

		const updatedDataElementList = dataElementList.map(dataElement => {
			if (dataElement.dataElementId === dataElementId && dataElement.parent_node_id === nodeId && dataElement.type === dataElementType) {

				const updatedPayload = { ...dataElement.savedPayload[0] }

				if (!updatedPayload.attachments) {
					updatedPayload.attachments = []
				}

				updatedPayload.attachments.push({
					filename,
					file_content
				})

				return {
					...dataElement,
					savedPayload: [updatedPayload],
				}
			}
			return dataElement

		})
		setDataElementList(updatedDataElementList)
		packageUpdate(updatedDataElementList, dataElementId, nodeId, dataElementType)
	}

	function handleRemoveAttachment(filename, dataElementId, nodeId, dataElementType) {

		const updatedDataElementList = dataElementList.map(dataElement => {
			if (dataElement.dataElementId === dataElementId && dataElement.parent_node_id === nodeId && dataElement.type === dataElementType) {

				for (let payload of dataElement.savedPayload) {
					if (payload.attachments) {
						payload.attachments = payload.attachments.filter(file => file.filename !== filename)
					}
				}
			}
			return dataElement

		})
		setDataElementList(updatedDataElementList)
		packageUpdate(updatedDataElementList, dataElementId, nodeId, dataElementType)
	}

	/*=================================================
		E-Form
	/================================================*/
	function handleFillEformBtnClick(dataElement, isReadOnly) {
		goTo(`/package/details/${currentPackage.id}/eform`,  {
			dataElement,
			isReadOnly: isReadOnly,
			currentPackage,
			currentFolder
		});
	}

	const renderDataElementCard = (dataElement) => {
		if (!dataElement.messageStoreId && dataElement.type === DATA_ElEMENT_TYPE.receiver_send) {
			return (
				<div key={`${dataElement.dataElementId}-${dataElement.type}`}>
					<PackageReceivingDataElementCard
						dataElement={dataElement}
						reload={reload}
						currentPackage={currentPackage}
					/>
				</div>
			)
		} else {
			return (
				<div key={`${dataElement.dataElementId}-${dataElement.type}`}>
					<VeridaptDataElementCard
						reload={reload}
						isPackageCopy={isPackageCopy}
						currentPackage={currentPackage}
						currentFolder={currentFolder}

						dataElement={dataElement}

						//Function
						handleRecipientChange={packageRecipientChange}
						handleOnBehalfOfChange={packageOnBehalfOfChange}

						onFillEformBtnClick={handleFillEformBtnClick}
						handleDataStructureUpload={handleDataStructureUpload}
						handleAttachmentsUpload={handleAttachmentsUpload}
						handleRemoveAttachment={handleRemoveAttachment}
					/>
				</div>
			)
		}
	}

	return (
		<>
			{/* Package Data Elements Section */}
			{milestonesList.length > 0 && packageDataElement?.length > 0 && (
				<div className="veridapt-data__package-data-element-wrapper">
					<div className="veridapt-data__package-container">
						<div className="veridapt-data__package-data-element-header veridapt-data--cursor" onClick={() => setPackageDeIsOpen(!packageDeIsOpen)}>
							<IconDataElement />
							<div className="veridapt-data__package-data-element-title">{`Data Element (${packageDataElement?.length})`}</div>
							<div className="veridapt-data__milestone-arrow" ><IconArrowDown /></div>
						</div>
						{packageDeIsOpen && (
							<div className="veridapt-data__package-data-element-container">
								{dataElementList?.filter(dataElement => dataElement.parent_node_id === currentPackage.id)
									.map((dataElement) => renderDataElementCard(dataElement))}
							</div>
						)}
					</div>
				</div>
			)}

			{milestonesList.length === 0 && (
				<div className="package-data__data-element-container">
					<div className="package-data__data-element-header">
						<IconDataElement />
						<div className="package-data__data-element-title">{`Data Element (${packageDataElement?.length})`}</div>
					</div>

					<div className="package-data__data-element-content">
						{dataElementList?.filter(dataElement => dataElement.parent_node_id === currentPackage.id)
							.map((dataElement) => renderDataElementCard(dataElement))}
					</div>
				</div>
			)}

			{milestonesList.length > 0 && (
				<div className="veridapt-data__header">
					<IconMilestonesHeader />
					<div className="milestones-header__text">{`Milestones (${milestonesList.length})`}</div>
				</div>
			)}

			{/* Milestones Quicklink */}
			{milestonesList.length > 1 && (
				<div className="veridapt-data__quicklink" data-testid="quicklink-stepper">
					<QuickLinkStepper
						labelList={milestonesList}
						activeStep={selectedMilestone}
						setActiveStep={quickLinkSetActiveStep}
						stepBy={5}
					/>
				</div>
			)}

			{/* Milestones details card */}
			{milestonesList.map((milestone) => (
				<div className="veridapt-data__milestones-container" key={milestone.id} ref={refs[milestone.id]}>
					<div className={"veridapt-data__milestones-header" + (milestone.participantOrgIds?.some(participant => participant.id === user.organizationId) && milestonesList.length > 1  ? " veridapt-data--cursor" :"" )}
						onClick={() => toggleExpand(milestone.id, milestone.participantOrgIds)}>
						<div
							className={`veridapt-data__milestone-title ${selectedMilestone === milestone.id ? "veridapt-data__milestone-title--active" : "veridapt-data__milestone-title--inactive"}`}
							data-testid="milestone-title"
						>
							<div className="package-milestone-icon"><IconMilestonesFlag /></div>
							<div className="tooltip">
								<MultiClamp clamp={1}>{milestone.name}</MultiClamp>
								<span className="tooltiptext__package-milestone">{milestone.name}</span>
							</div>
						</div>
						{milestone.status && (
							<div
								className={`veridapt-badge ${selectedMilestone === milestone.id ? "veridapt-badge--active" : "veridapt-badge--inactive"}`}
								data-testid="milestone-badge"
							>
								{milestone.status}
							</div>
						)}
						<div className="veridapt-data__milestone-participants tooltip">
							<MultiClamp clamp={1}>{milestone.participantOrgIds?.map(participant => participant.name).join(", ")}</MultiClamp>
							<span className="tooltiptext__package-milestone">
								{milestone.participantOrgIds?.map(participant => <div key={participant.id}>{participant.name}</div>)}
							</span>
						</div>
						{milestone.participantOrgIds?.some(participant => participant.id === user.organizationId) && milestonesList.length > 1 && <div className="veridapt-data__milestone-arrow"><IconArrowDown /></div> }
					</div>

					{/* Activity details card */}
					{activeStep === milestone.id && (
						<>
							<div className="veriapt-data__milestones-contents" data-testid="milestones-content">
								<div className="veridapt-data__milestone-data-element-container">
									{dataElementList?.filter(dataElement => dataElement.parent_node_id === milestone.id)
										.map((dataElement) => renderDataElementCard(dataElement))}
								</div>
								<div className="veridapt-data__activity-container">
									<div className="veridapt-data__activity-header">
										<IconActivity />
										{`Activities (${milestone.activities?.length || 0})`}
									</div>
									<div className="veridapt-data__activity-content">
										{milestone.activities?.map(activity => (
											<div key={activity.id}>
												<div className="veridapt-data__activity-info-container">
													<div className="veridapt-data__activity-name tooltip">
														<MultiClamp clamp={1}>{activity.name}</MultiClamp>
														<span className="tooltiptext__package-milestone">{activity.name}</span>
													</div>
													{activity.status && <div className="veridapt-badge veridapt-badge--inactive">{activity.status}</div>}
													<div className="veridapt-data__activity-participants tooltip">
														<MultiClamp clamp={1}>{activity.participantOrgIds?.map(participant => participant.name).join(", ")}</MultiClamp>
														<span className="tooltiptext__package-milestone">{activity.participantOrgIds?.map(participant => <div key={participant.id}>{participant.name}</div>)}</span>
													</div>
												</div>
												<div className="veridapt-data__activity-data-element-container">
													{dataElementList?.filter(dataElement => dataElement.parent_node_id === activity.id)
														.map((dataElement) => renderDataElementCard(dataElement))}
												</div>
											</div>
										))}
									</div>
								</div>
							</div>
						</>
					)}
				</div>
			))
			}
		</>
	)
}

VeridaptDataView.propTypes = {
	currentPackage: PropTypes.any,
	currentFolder: PropTypes.any,
	reload: PropTypes.func
}

export default VeridaptDataView
